import { SphereIconButton } from "@components/common/sphere-icon-button";
import { FileUploadTask } from "@custom-types/file-upload-types";
import {
  blue,
  CloseCancelErrorIcon,
  FileDocumentIcon,
  neutral,
  red,
} from "@flat-ui";
import { BackgroundTaskState } from "@faro-lotv/service-wires";
import { useCancelUpload } from "@hooks/upload-tasks/use-cancel-upload";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { useAppDispatch } from "@store/store-helper";
import { removeOne } from "@store/upload-tasks/upload-tasks-slice";
import { DEFAULT_TEXT_FONT_SIZE, withEllipsis } from "@styles/common-styles";

interface Props {
  /** The upload task to display the upload progress */
  task: FileUploadTask;

  /** True if the current element is the last one in the list */
  isLastElement: boolean;
}

/** The class of the cancel button */
const cancelButtonClass = "cancelButton";

/** The class of the progress bar */
const progressBarClass = "progressBar";

/** The class of the file name */
const fileNameClass = "fileName";

/** Renders a markup attachment upload task */
export function MarkupUploadingAttachmentItem({
  task,
  isLastElement,
}: Props): JSX.Element {
  const cancelUploadTask = useCancelUpload();
  const dispatch = useAppDispatch();

  async function cancelUpload(): Promise<void> {
    await cancelUploadTask(task.id);

    if (task.status === BackgroundTaskState.failed) {
      dispatch(removeOne(task.id));
    }
  }

  return (
    <Box
      data-testid="markup-side-panel-attachment-item-container"
      sx={{
        display: "flex",
        gap: "8px",
        borderBottom: !isLastElement ? 1 : 0,
        borderColor: "divider",
        padding: "8px",
        transition: "background-color 0.3s",
        alignItems: "center",
        "&:hover": {
          backgroundColor: neutral[100],
          cursor: "pointer",
          [`& .${fileNameClass}`]: {
            color: blue[500],
          },
          [`& .${cancelButtonClass}`]: {
            display: "block",
          },
          [`& .${progressBarClass}`]: {
            display: "none",
          },
        },
      }}
    >
      {/* File Icon */}
      <Box sx={{ height: "43px", display: "flex", alignItems: "center" }}>
        <FileDocumentIcon
          sx={{ width: "60px", height: "32px", color: neutral[600] }}
        />
      </Box>

      <Stack sx={{ gap: 0.5, width: "calc(100% - 110px)" }}>
        {/* File name */}
        <Typography
          className={fileNameClass}
          sx={{
            fontSize: DEFAULT_TEXT_FONT_SIZE,
            color: neutral[800],
            ...withEllipsis,
          }}
        >
          <var>{task.fileName}</var>
        </Typography>

        {/* Failed message or progress bar */}
        {task.status === BackgroundTaskState.failed ? (
          <Typography
            sx={{
              color: red[500],
              fontWeight: "600",
              fontSize: "10px",
              ...withEllipsis,
            }}
          >
            {task.errorMessage ?? "Unexpected error. Please contact support."}
          </Typography>
        ) : (
          <LinearProgress
            value={task.progress}
            variant={task.progress > 0 ? "determinate" : "indeterminate"}
          />
        )}
      </Stack>

      {/* Cancel icon */}
      <Box sx={{ textAlign: "center", width: "50px" }}>
        {task.progress > 0 && task.status !== BackgroundTaskState.failed && (
          <var
            className={progressBarClass}
            style={{
              fontSize: DEFAULT_TEXT_FONT_SIZE,
              color: neutral[600],
            }}
          >
            {/* Round because CoreFileUploader sends "progress: 0.00001" when
                upload starts, to switch to determinate progress bar. */}
            {Math.round(task.progress)}%
          </var>
        )}

        <Box
          className={cancelButtonClass}
          sx={{
            display:
              task.status === BackgroundTaskState.failed ? "block" : "none",
          }}
        >
          <SphereIconButton
            component={CloseCancelErrorIcon}
            onClick={cancelUpload}
            iconSize="18px"
          />
        </Box>
      </Box>
    </Box>
  );
}
